import {getCurrentDifficulty} from '@domain/pom'
import {
  DataFetchingPlaceholder,
  ReferencesLink,
  REFERENCES_LINK_TITLE_MARGIN_BOTTOM,
  SectionContainer,
  useSection,
  useSectionSubmissionsDownload,
  usePlantUpmId,
  useSectionId,
  usePlantContext,
  QuestionStateProvider,
  usePomNavigation
} from '@features/pom'
import {
  PageTitle,
  PageContainer,
  TextOnBackground,
  BackToLink,
  buttonOnBlueSx
} from '@hconnect/uikit/src/lib2'
import {FileDownload} from '@mui/icons-material'
import {Box, Button, Stack} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

export const PlantSectionPage: React.FC = () => {
  const {t} = useTranslation()
  const plantId = usePlantUpmId()
  const nav = usePomNavigation()
  const sectionId = useSectionId()
  const {data: section, isLoading: isLoadingSection} = useSection()
  const {isPending: sectionSubmissionsDownloadIsLoading, mutate: downloadSectionSubmissions} =
    useSectionSubmissionsDownload(plantId, sectionId)
  const {data} = usePlantContext()

  const navigate = useNavigate()

  return (
    <PageContainer>
      <BackToLink
        targetName={t('navigation.backTo', {target: t('navigation.dashboard')})}
        target={nav.buildDashboardPath(plantId)}
        navigate={navigate}
      />
      <Stack pb={2} direction={'row'} justifyContent={'space-between'}>
        <Stack>
          <PageTitle
            data-test-id="plant-section-page-title"
            mb={section?.guidanceLink ? 0 : REFERENCES_LINK_TITLE_MARGIN_BOTTOM}
          >
            {section?.name}
            <TextOnBackground variant="caption" ml={1} contrast="low">
              {section?.id}
            </TextOnBackground>
          </PageTitle>
          {data?.guidanceLink && (
            <ReferencesLink guidanceLink={data.guidanceLink} color="primary.contrastText" />
          )}
        </Stack>
        <Box>
          <Button
            variant={'text'}
            color={'primary'}
            sx={buttonOnBlueSx}
            disabled={sectionSubmissionsDownloadIsLoading}
            onClick={() => downloadSectionSubmissions()}
          >
            <FileDownload sx={{mr: 0.5}} />
            {t('button.download')}
          </Button>
        </Box>
      </Stack>

      {section ? (
        <QuestionStateProvider
          data={{activeDifficulty: getCurrentDifficulty(section.difficultySummaries)}}
        >
          <SectionContainer section={section} />
        </QuestionStateProvider>
      ) : (
        <DataFetchingPlaceholder
          isLoading={isLoadingSection}
          errorMessage={t('errors.sectionNotFetched')}
        />
      )}
    </PageContainer>
  )
}
